import { format } from 'date-fns';
import { useMemo } from 'react';

import { generateMonthMarkers } from './utils';

interface TimelineHeaderProps {
  startDate: Date; // Visible start date for the current view
  endDate: Date; // Visible end date for the current view
}

export const TimelineHeader = ({ startDate, endDate }: TimelineHeaderProps) => {
  const monthMarkers = useMemo(
    () => generateMonthMarkers(startDate, endDate),
    [startDate, endDate],
  );

  // Format the visible date range for display
  const visibleRange = useMemo(() => {
    const startStr = format(startDate, 'MMM d, yyyy');
    const endStr = format(endDate, 'MMM d, yyyy');
    return `${startStr} - ${endStr}`;
  }, [startDate, endDate]);

  return (
    <div className="sticky top-0 z-30 h-10 border-b border-gray-200 bg-white/95 backdrop-blur-sm flex shadow-sm will-change-transform">
      {/* Fixed provider column header */}
      <div className="sticky left-0 w-52 bg-white/95 backdrop-blur-sm border-r border-gray-200 flex justify-between items-center px-3 flex-shrink-0 z-10 h-full shadow-sm">
        <div className="text-xs font-semibold text-gray-600">Provider</div>
        <div className="text-xs font-medium text-gray-500">
          <span className="inline-flex h-5 items-center rounded-sm bg-gray-50 px-1.5 text-[10px] text-gray-500 border border-gray-100">
            Drag to Pan
          </span>
        </div>
      </div>

      {/* Scrollable timeline header area */}
      <div className="relative flex-grow h-full">
        {/* Date range repositioned to top right - not overlapping with markers */}
        <div className="absolute top-0 right-0 px-2 py-1 z-20 pointer-events-none">
          <div className="text-xs font-medium text-gray-600 bg-white/90 px-2 py-0.5 rounded-md shadow-sm border border-gray-100 backdrop-blur-sm transition-all duration-300">
            {visibleRange}
          </div>
        </div>

        {/* Month markers */}
        <div className="absolute inset-0 pt-1">
          {monthMarkers.map((marker, index) => (
            <div
              key={`marker-${index}-${marker.label}`}
              className="absolute top-0 bottom-0 flex flex-col items-center justify-end pointer-events-none transition-transform duration-300 ease-in-out will-change-transform"
              style={{ left: `${marker.position}%` }}
            >
              {/* Show a vertical grid line */}
              <div className="absolute top-0 bottom-0 w-px bg-gray-100 z-0" />

              {/* Label for the month/year - positioned on bottom */}
              <span className="text-[10px] font-medium text-gray-500 mb-0.5 whitespace-nowrap z-10 bg-white/90 px-1.5 py-0.5 rounded-sm shadow-sm border border-gray-50 transition-all">
                {marker.label}
              </span>

              {/* Tick mark */}
              <div className="w-px h-2 bg-gray-300 z-10" />
            </div>
          ))}
        </div>

        {/* Base timeline axis */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gray-300" />
      </div>
    </div>
  );
};
